import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 11: VEER
 * Style: Bold Masculine — Dark charcoal with warm amber accents
 * Best for: Indian men's fashion — Sherwanis, Kurta Pajamas, Nehru Jackets, Formal Suits
 */
const Template11 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const [hoveredProduct, setHoveredProduct] = useState(null);
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const touchStartX = useRef(0);

    // Categories
    const categories = [
        { name: 'Sherwanis', image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=400', count: '24 Styles' },
        { name: 'Kurta Pajamas', image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400', count: '36 Styles' },
        { name: 'Nehru Jackets', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400', count: '18 Styles' },
        { name: 'Formal Suits', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400', count: '28 Styles' },
    ];

    // Products
    const products = [
        { id: 1, name: 'Royal Black Sherwani', price: '\u20B924,999', badge: 'Bestseller', image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Ivory Silk Kurta Set', price: '\u20B95,999', image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Charcoal Nehru Jacket', price: '\u20B97,499', badge: 'New', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Navy Formal Suit', price: '\u20B918,999', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Gold Brocade Sherwani', price: '\u20B929,999', badge: 'Premium', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Sage Linen Kurta', price: '\u20B94,499', image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Burgundy Bandhgala', price: '\u20B912,999', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Classic Black Tuxedo', price: '\u20B922,999', badge: 'Limited', image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    // Style Guide
    const styleGuide = [
        { title: 'The Wedding Edit', description: 'Commanding sherwanis and bandhgalas for the groom who leads.', tip: 'Pair with mojaris and a statement brooch.' },
        { title: 'Festive Dressing', description: 'Rich kurtas and Nehru jackets that own every celebration.', tip: 'Layer a brocade jacket over a silk kurta.' },
        { title: 'Power Formals', description: 'Tailored suits built for boardrooms and beyond.', tip: 'A well-fitted charcoal suit never fails.' },
    ];

    // Testimonials
    const testimonials = [
        { text: 'The sherwani fit was impeccable. Every detail spoke of craftsmanship that you simply cannot find elsewhere. I felt like royalty on my wedding day.', name: 'Arjun Malhotra', city: 'Delhi' },
        { text: 'VEER understands what modern Indian men want. The Nehru jacket I ordered was perfectly tailored and arrived in pristine packaging.', name: 'Vikram Patel', city: 'Mumbai' },
        { text: 'From the fabric quality to the stitching precision, everything exceeded my expectations. The formal suit collection is absolutely world-class.', name: 'Rahul Sharma', city: 'Bangalore' },
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    return (
        <div style={{ fontFamily: "'Source Sans 3', -apple-system, BlinkMacSystemFont, sans-serif" }} className="w-full min-h-screen">
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Source+Sans+3:wght@300;400;600&display=swap');
            `}</style>

            {/* ===== SECTION 1: DARK HERO ===== */}
            <section className="relative min-h-screen w-full overflow-hidden flex items-center justify-center" style={{ backgroundColor: '#1C1917' }}>
                {/* Background Image */}
                <img
                    src="https://images.pexels.com/photos/3363204/pexels-photo-3363204.jpeg?auto=compress&cs=tinysrgb&w=1200"
                    alt="Indian menswear"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ opacity: 0.4 }}
                />

                {/* Dark Gradient Overlay */}
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(to bottom, rgba(28,25,23,0.3) 0%, rgba(28,25,23,0.6) 50%, #1C1917 100%)'
                    }}
                />

                {/* Hero Content */}
                <div className="relative z-10 text-center px-4 sm:px-6">
                    <div className="w-12 h-0.5 mx-auto mb-6" style={{ backgroundColor: '#D97706' }} />
                    <p
                        className="text-xs sm:text-sm tracking-[0.4em] uppercase mb-4"
                        style={{ color: '#D97706', fontFamily: "'Oswald', sans-serif", fontWeight: 500 }}
                    >
                        Premium Indian Menswear
                    </p>
                    <h1
                        className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 tracking-wider"
                        style={{
                            fontFamily: "'Oswald', sans-serif",
                            color: '#FAFAF9',
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                        }}
                    >
                        DEFINE YOUR STYLE
                    </h1>
                    <p
                        className="text-base sm:text-lg lg:text-xl font-light mb-10 max-w-lg mx-auto"
                        style={{
                            color: '#A8A29E',
                            fontFamily: "'Source Sans 3', sans-serif",
                        }}
                    >
                        Sherwanis. Suits. Kurtas. Crafted for men who command attention.
                    </p>
                    <button
                        onClick={() => navigate('/template11/shop')}
                        className="inline-flex items-center gap-3 px-8 py-3.5 sm:px-10 sm:py-4 text-sm tracking-[0.2em] uppercase transition-all duration-300"
                        style={{
                            backgroundColor: '#D97706',
                            border: '1px solid #D97706',
                            color: '#1C1917',
                            fontFamily: "'Oswald', sans-serif",
                            fontWeight: 600,
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#F59E0B';
                            e.currentTarget.style.borderColor = '#F59E0B';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#D97706';
                            e.currentTarget.style.borderColor = '#D97706';
                        }}
                    >
                        Shop Collection <ArrowRight className="w-4 h-4" />
                    </button>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
                    <div className="w-px h-12 opacity-40" style={{ backgroundColor: '#D97706' }}>
                        <div
                            className="w-full h-1/2"
                            style={{
                                backgroundColor: '#D97706',
                                animation: 'veerScrollPulse 2s ease-in-out infinite'
                            }}
                        />
                    </div>
                </div>
                <style>{`
                    @keyframes veerScrollPulse {
                        0%, 100% { opacity: 0.3; transform: translateY(0); }
                        50% { opacity: 1; transform: translateY(12px); }
                    }
                `}</style>
            </section>

            {/* ===== SECTION 2: CATEGORIES ===== */}
            <section className="py-16 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: '#1C1917' }}>
                <div className="max-w-[1400px] mx-auto">
                    <div className="text-center mb-12 sm:mb-16">
                        <div className="w-10 h-0.5 mx-auto mb-4" style={{ backgroundColor: '#D97706' }} />
                        <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: '#D97706', fontFamily: "'Oswald', sans-serif", fontWeight: 500 }}>
                            Collections
                        </p>
                        <h2
                            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-wide"
                            style={{ fontFamily: "'Oswald', sans-serif", color: '#FAFAF9', textTransform: 'uppercase' }}
                        >
                            SHOP BY CATEGORY
                        </h2>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                        {categories.map((cat, i) => (
                            <div
                                key={i}
                                onClick={() => navigate('/template11/shop')}
                                onMouseEnter={() => setHoveredCategory(i)}
                                onMouseLeave={() => setHoveredCategory(null)}
                                className="relative cursor-pointer overflow-hidden group"
                                style={{ aspectRatio: '3 / 4' }}
                            >
                                <img
                                    src={cat.image}
                                    alt={cat.name}
                                    className="w-full h-full object-cover transition-transform duration-500"
                                    style={{ transform: hoveredCategory === i ? 'scale(1.08)' : 'scale(1)' }}
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{ background: 'linear-gradient(to top, rgba(28,25,23,0.9) 0%, rgba(28,25,23,0.2) 50%, transparent 100%)' }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                                    <h3
                                        className="text-lg sm:text-xl font-semibold mb-1"
                                        style={{ fontFamily: "'Oswald', sans-serif", color: '#FAFAF9', textTransform: 'uppercase', letterSpacing: '1px' }}
                                    >
                                        {cat.name}
                                    </h3>
                                    <p className="text-xs" style={{ color: '#D97706', fontFamily: "'Source Sans 3', sans-serif" }}>
                                        {cat.count}
                                    </p>
                                </div>
                                <div
                                    className="absolute inset-0 transition-opacity duration-300"
                                    style={{
                                        border: hoveredCategory === i ? '2px solid #D97706' : '2px solid transparent',
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: PRODUCTS ===== */}
            <section className="py-16 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: '#292524' }}>
                <div className="max-w-[1400px] mx-auto">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-12">
                        <div>
                            <div className="w-10 h-0.5 mb-4" style={{ backgroundColor: '#D97706' }} />
                            <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: '#D97706', fontFamily: "'Oswald', sans-serif", fontWeight: 500 }}>
                                Curated Selection
                            </p>
                            <h2
                                className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-wide"
                                style={{ fontFamily: "'Oswald', sans-serif", color: '#FAFAF9', textTransform: 'uppercase' }}
                            >
                                FEATURED PIECES
                            </h2>
                        </div>
                        <button
                            onClick={() => navigate('/template11/shop')}
                            className="mt-4 sm:mt-0 inline-flex items-center gap-2 text-sm tracking-[0.15em] uppercase transition-colors duration-300"
                            style={{ color: '#D97706', fontFamily: "'Oswald', sans-serif", fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#F59E0B')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#D97706')}
                        >
                            View All <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate(`/template11/product/${product.id}`)}
                                onMouseEnter={() => setHoveredProduct(product.id)}
                                onMouseLeave={() => setHoveredProduct(null)}
                                className="cursor-pointer overflow-hidden"
                                style={{
                                    backgroundColor: '#1C1917',
                                    border: `1px solid ${hoveredProduct === product.id ? '#D97706' : '#44403C'}`,
                                    transition: 'border-color 0.3s ease',
                                }}
                            >
                                {/* Image */}
                                <div className="relative overflow-hidden" style={{ aspectRatio: '3 / 4' }}>
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-transform duration-500"
                                        style={{ transform: hoveredProduct === product.id ? 'scale(1.05)' : 'scale(1)' }}
                                    />
                                    {product.badge && (
                                        <span
                                            className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider px-3 py-1"
                                            style={{ backgroundColor: '#D97706', color: '#1C1917', fontFamily: "'Source Sans 3', sans-serif" }}
                                        >
                                            {product.badge}
                                        </span>
                                    )}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center backdrop-blur-sm"
                                        style={{
                                            backgroundColor: 'rgba(28,25,23,0.6)',
                                            border: 'none',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <Heart
                                            size={18}
                                            style={{
                                                color: wishlist[product.id] ? '#D97706' : '#FAFAF9',
                                                fill: wishlist[product.id] ? '#D97706' : 'none',
                                                transition: 'all 0.3s ease',
                                            }}
                                        />
                                    </button>
                                </div>
                                {/* Info */}
                                <div className="p-4">
                                    <h3
                                        className="text-sm font-semibold mb-2 truncate"
                                        style={{ fontFamily: "'Source Sans 3', sans-serif", color: '#FAFAF9' }}
                                    >
                                        {product.name}
                                    </h3>
                                    <span
                                        className="text-base font-semibold"
                                        style={{ fontFamily: "'Source Sans 3', sans-serif", color: '#D97706' }}
                                    >
                                        {product.price}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: STYLE GUIDE ===== */}
            <section className="py-16 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: '#1C1917' }}>
                <div className="max-w-[1400px] mx-auto">
                    <div className="text-center mb-12 sm:mb-16">
                        <div className="w-10 h-0.5 mx-auto mb-4" style={{ backgroundColor: '#D97706' }} />
                        <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: '#D97706', fontFamily: "'Oswald', sans-serif", fontWeight: 500 }}>
                            The Gentleman's Playbook
                        </p>
                        <h2
                            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-wide"
                            style={{ fontFamily: "'Oswald', sans-serif", color: '#FAFAF9', textTransform: 'uppercase' }}
                        >
                            STYLE GUIDE
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                        {styleGuide.map((item, i) => (
                            <div
                                key={i}
                                className="p-6 sm:p-8"
                                style={{
                                    backgroundColor: '#292524',
                                    borderLeft: '3px solid #D97706',
                                }}
                            >
                                <span
                                    className="text-5xl font-bold block mb-4"
                                    style={{ fontFamily: "'Oswald', sans-serif", color: '#44403C' }}
                                >
                                    0{i + 1}
                                </span>
                                <h3
                                    className="text-xl sm:text-2xl font-semibold mb-3"
                                    style={{ fontFamily: "'Oswald', sans-serif", color: '#FAFAF9', textTransform: 'uppercase', letterSpacing: '1px' }}
                                >
                                    {item.title}
                                </h3>
                                <p
                                    className="text-sm mb-4 leading-relaxed"
                                    style={{ fontFamily: "'Source Sans 3', sans-serif", color: '#A8A29E' }}
                                >
                                    {item.description}
                                </p>
                                <div className="pt-4" style={{ borderTop: '1px solid #44403C' }}>
                                    <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#D97706', fontFamily: "'Oswald', sans-serif", fontWeight: 500 }}>
                                        Pro Tip
                                    </p>
                                    <p className="text-sm italic" style={{ color: '#A8A29E', fontFamily: "'Source Sans 3', sans-serif" }}>
                                        {item.tip}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: TESTIMONIALS ===== */}
            <section
                className="py-16 sm:py-24 px-4 sm:px-6"
                style={{ backgroundColor: '#292524' }}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                <div className="max-w-[800px] mx-auto text-center">
                    <div className="w-10 h-0.5 mx-auto mb-4" style={{ backgroundColor: '#D97706' }} />
                    <p className="text-xs tracking-[0.3em] uppercase mb-8" style={{ color: '#D97706', fontFamily: "'Oswald', sans-serif", fontWeight: 500 }}>
                        What Our Clients Say
                    </p>

                    <div className="relative min-h-[200px]">
                        {testimonials.map((t, i) => (
                            <div
                                key={i}
                                className="absolute inset-0 transition-opacity duration-500"
                                style={{ opacity: activeTestimonial === i ? 1 : 0, pointerEvents: activeTestimonial === i ? 'auto' : 'none' }}
                            >
                                <div className="flex justify-center gap-1 mb-6">
                                    {[...Array(5)].map((_, si) => (
                                        <Star key={si} size={16} style={{ color: '#D97706', fill: '#D97706' }} />
                                    ))}
                                </div>
                                <p
                                    className="text-lg sm:text-xl font-light mb-8 leading-relaxed"
                                    style={{ fontFamily: "'Source Sans 3', sans-serif", color: '#FAFAF9' }}
                                >
                                    "{t.text}"
                                </p>
                                <div>
                                    <p
                                        className="text-sm font-semibold tracking-wider uppercase"
                                        style={{ fontFamily: "'Oswald', sans-serif", color: '#D97706' }}
                                    >
                                        {t.name}
                                    </p>
                                    <p className="text-xs mt-1" style={{ color: '#A8A29E', fontFamily: "'Source Sans 3', sans-serif" }}>
                                        {t.city}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Dots */}
                    <div className="flex justify-center gap-3 mt-8">
                        {testimonials.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setActiveTestimonial(i)}
                                style={{
                                    width: activeTestimonial === i ? '24px' : '8px',
                                    height: '8px',
                                    backgroundColor: activeTestimonial === i ? '#D97706' : '#44403C',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                }}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 6: NEWSLETTER ===== */}
            <section className="py-16 sm:py-24 px-4 sm:px-6" style={{ backgroundColor: '#1C1917' }}>
                <div className="max-w-[600px] mx-auto text-center">
                    <div className="w-10 h-0.5 mx-auto mb-4" style={{ backgroundColor: '#D97706' }} />
                    <h2
                        className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 tracking-wide"
                        style={{ fontFamily: "'Oswald', sans-serif", color: '#FAFAF9', textTransform: 'uppercase' }}
                    >
                        JOIN THE BROTHERHOOD
                    </h2>
                    <p
                        className="text-sm mb-8"
                        style={{ fontFamily: "'Source Sans 3', sans-serif", color: '#A8A29E' }}
                    >
                        Get first access to new drops, style tips, and exclusive offers. No spam, just substance.
                    </p>
                    <form
                        onSubmit={(e) => { e.preventDefault(); if (email.trim()) setEmail(''); }}
                        className="flex flex-col sm:flex-row gap-0"
                    >
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            className="flex-1 px-5 py-4 text-sm outline-none"
                            style={{
                                backgroundColor: '#292524',
                                border: '1px solid #44403C',
                                borderRight: 'none',
                                color: '#FAFAF9',
                                fontFamily: "'Source Sans 3', sans-serif",
                            }}
                        />
                        <button
                            type="submit"
                            className="px-8 py-4 text-sm tracking-[0.15em] uppercase transition-colors duration-300"
                            style={{
                                backgroundColor: '#D97706',
                                border: '1px solid #D97706',
                                color: '#1C1917',
                                fontFamily: "'Oswald', sans-serif",
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#F59E0B';
                                e.currentTarget.style.borderColor = '#F59E0B';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#D97706';
                                e.currentTarget.style.borderColor = '#D97706';
                            }}
                        >
                            Subscribe
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default Template11;
